import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Row,
  Col,
  Card,
  Typography,
  Tag,
  Spin,
  Empty,
  Button,
} from 'antd';
import {
  BarChartOutlined,
  BookOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS, ASSIGNMENT_STATUS_COLORS } from '@/constants';
import { useAuthStore } from '@/stores/authStore';
import { useHasRealTeachers } from '@/hooks/useHasRealTeachers';
import { daysUntil, isOverdue, formatShortDate } from '@/utils/date';
import type { ApiResponse, DeckAssignment } from '@/types';

const { Title, Text, Paragraph } = Typography;

type FilterStatus = 'ALL' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

function DeadlineDisplay({ deadline }: { deadline: string | null | undefined }) {
  if (!deadline) return null;

  const overdue = isOverdue(deadline);
  const days = daysUntil(deadline);

  if (overdue) {
    return (
      <Text style={{ color: '#ef4444', fontSize: 13, fontWeight: 500 }}>
        <ExclamationCircleFilled style={{ marginRight: 4 }} />
        Overdue
      </Text>
    );
  }

  if (days === null) return null;

  const color = days <= 2 ? '#f59e0b' : '#64748b';
  const label =
    days === 0
      ? 'Due today'
      : days === 1
        ? 'Due tomorrow'
        : `${days} days left`;

  return (
    <Text style={{ color, fontSize: 13 }}>
      <ClockCircleOutlined style={{ marginRight: 4 }} />
      {label}
    </Text>
  );
}

export default function DecksPage() {
  const [searchParams] = useSearchParams();
  const initialFilter = (searchParams.get('filter') as FilterStatus) ?? 'ALL';
  const [filter, setFilter] = useState<FilterStatus>(initialFilter);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const hasRealTeachers = useHasRealTeachers();

  const { data, isLoading } = useQuery({
    queryKey: ['student', 'decks'],
    queryFn: () =>
      api.get<never, ApiResponse<{ data: DeckAssignment[]; meta: { cursor?: string; hasMore: boolean } }>>(
        API_ENDPOINTS.STUDENT_DECKS,
      ),
  });

  const assignments: DeckAssignment[] = data?.data?.data ?? [];

  const filtered =
    filter === 'ALL'
      ? assignments
      : assignments.filter((a) => a.status === filter);

  const handleDeckClick = (assignment: DeckAssignment) => {
    if (assignment.status === 'COMPLETED') {
      navigate(`/student/decks/${assignment.deckId}/review?mode=view`);
    } else {
      navigate(`/student/decks/${assignment.deckId}/review`);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          My Decks
        </Title>
        <Text type="secondary" style={{ fontSize: 15 }}>
          Welcome back, {user?.name ?? 'there'}! Ready to learn something new?
        </Text>
      </div>

      <div style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['ALL', 'PENDING', 'IN_PROGRESS', 'COMPLETED'] as FilterStatus[]).map((val) => (
          <Button
            key={val}
            type={filter === val ? 'primary' : 'default'}
            onClick={() => setFilter(val)}
            size="middle"
          >
            {val === 'ALL' ? 'All' : val === 'IN_PROGRESS' ? 'In Progress' : val.charAt(0) + val.slice(1).toLowerCase()}
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card style={{ borderRadius: 12, textAlign: 'center', padding: '40px 0' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              filter === 'ALL' ? (
                <div>
                  <Text style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    No decks assigned yet
                  </Text>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    {hasRealTeachers
                      ? 'Ask your teacher for a deck!'
                      : 'New decks will appear here soon!'}
                  </Text>
                </div>
              ) : (
                <Text style={{ fontSize: 15 }}>
                  No {filter.toLowerCase().replace('_', ' ')} decks
                </Text>
              )
            }
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((assignment) => {
            const deck = assignment.deck;
            const isCompleted = assignment.status === 'COMPLETED';
            const statusStyle = ASSIGNMENT_STATUS_COLORS[assignment.status];
            const deadline = assignment.deadline ?? deck?.deadline;

            return (
              <Col xs={24} sm={12} lg={8} key={assignment.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: 12,
                    cursor: 'pointer',
                    height: '100%',
                  }}
                  actions={
                    isCompleted
                      ? [
                          <Button
                            key="results"
                            type="primary"
                            icon={<BarChartOutlined />}
                            onClick={() =>
                              navigate(`/student/sessions/${assignment.lastSession?.id}/complete`)
                            }
                            disabled={!assignment.lastSession?.id}
                            style={{ margin: '0 8px', background: '#6366f1', borderColor: '#6366f1' }}
                          >
                            View Results
                          </Button>,
                          <Button
                            key="review"
                            icon={<EyeOutlined />}
                            onClick={() => handleDeckClick(assignment)}
                            style={{ margin: '0 8px' }}
                          >
                            Review Cards
                          </Button>,
                        ]
                      : [
                          <Button
                            key="action"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleDeckClick(assignment)}
                            style={{ margin: '0 16px' }}
                          >
                            Start
                          </Button>,
                        ]
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: '#f0f5ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                          color: '#3b82f6',
                          flexShrink: 0,
                        }}
                      >
                        <BookOutlined />
                      </div>
                      <Text
                        strong
                        style={{
                          fontSize: 16,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {deck?.topicName ?? 'Untitled Deck'}
                      </Text>
                    </div>

                    <Paragraph
                      type="secondary"
                      ellipsis={{ rows: 2 }}
                      style={{ fontSize: 13, marginBottom: 0, minHeight: 40 }}
                    >
                      {deck?.description ?? ''}
                    </Paragraph>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Tag style={{ margin: 0, fontSize: 12 }}>
                        {deck?.cardCount ?? 0} cards
                      </Tag>
                      <Tag
                        style={{
                          margin: 0,
                          color: statusStyle?.color ?? '#64748b',
                          background: statusStyle?.bg ?? '#f1f5f9',
                          border: 'none',
                          fontWeight: 500,
                          fontSize: 12,
                        }}
                      >
                        {assignment.status.replace('_', ' ')}
                      </Tag>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 4,
                        minHeight: 22,
                      }}
                    >
                      {!deck?.teacher?.isDemoTeacher && (
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {deck?.teacher?.name ?? 'Teacher'}
                        </Text>
                      )}
                      {!isCompleted && <DeadlineDisplay deadline={deadline} />}
                      {isCompleted && deadline && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatShortDate(deadline)}
                        </Text>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}
